import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, UserGroupIcon, MapIcon } from '@heroicons/react/24/outline';

/**
 * TableStatusGrid - Visual grid showing table status and occupancy with Premium Light Theme
 */
const TableStatusGrid = ({ tables, summary }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'occupied': return 'bg-orange-50 text-orange-600 border-orange-100';
      case 'reserved': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'inactive': return 'bg-gray-50 text-gray-400 border-gray-100';
      default: return 'bg-gray-50 text-gray-400 border-gray-100';
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm relative overflow-hidden transition-all hover:shadow-xl hover:shadow-gray-200/20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
        <div>
           <h3 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
             <MapIcon className="h-6 w-6 text-orange-500" />
             Floor Orchestration
           </h3>
           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Real-time table occupancy & session tracking</p>
        </div>

        <div className="flex bg-gray-50 p-1.5 rounded-2xl border border-gray-100 self-start">
          {['All', 'Active', 'Idle'].map(filter => (
             <button key={filter} className={`px-5 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
               filter === 'All' ? 'bg-white text-gray-900 shadow-md ring-1 ring-gray-100' : 'text-gray-400 hover:text-gray-600'
             }`}>{filter}</button>
          ))}
        </div>
      </div>

      {/* Summary Chips */}
      <div className="flex flex-wrap gap-6 mb-12">
        <div className="px-8 py-5 bg-gray-50/30 border border-gray-100 rounded-[28px] group hover:bg-white hover:shadow-xl hover:shadow-gray-200/20 transition-all duration-500">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">Occupancy</p>
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-black text-gray-900 tracking-tighter leading-none">{summary.occupancyRate}%</span>
            <span className="text-[10px] font-bold text-gray-300 uppercase leading-none">{summary.occupied} / {summary.totalTables} Live</span>
          </div>
        </div>
        <div className="px-8 py-5 bg-gray-50/30 border border-gray-100 rounded-[28px] group hover:bg-white hover:shadow-xl hover:shadow-gray-200/20 transition-all duration-500">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">Avg Session</p>
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-black text-gray-900 tracking-tighter leading-none">{formatDuration(summary.avgSessionDuration)}</span>
            <span className="text-[10px] font-black text-emerald-500 uppercase leading-none">Peak Perf</span>
          </div>
        </div>
      </div>

      {/* Table grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
        {tables.map((table, index) => (
          <motion.div
            key={table.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.02, type: "spring", stiffness: 200 }}
            className={`group relative p-6 rounded-[32px] border-2 transition-all duration-500 hover:shadow-2xl flex flex-col justify-between aspect-square cursor-pointer ${
              table.status === 'occupied' ? 'bg-orange-50/30 border-orange-100 hover:border-orange-200 hover:shadow-orange-100/30' :
              table.status === 'reserved' ? 'bg-blue-50/30 border-blue-100 hover:border-blue-200 hover:shadow-blue-100/30' :
              table.status === 'available' ? 'bg-emerald-50/20 border-emerald-50 hover:border-emerald-100 hover:shadow-emerald-100/30' :
              'bg-gray-50 border-gray-100 hover:border-gray-200 hover:shadow-gray-200/30'
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <span className={`text-3xl font-black tracking-tighter block mb-1 group-hover:scale-110 transition-transform ${
                  table.status === 'occupied' ? 'text-orange-600' :
                  table.status === 'reserved' ? 'text-blue-600' :
                  table.status === 'available' ? 'text-emerald-600' :
                  'text-gray-400'
                }`}>
                  T{table.tableNumber}
                </span>
                <div className="flex items-center gap-1.5 opacity-60">
                   <UserGroupIcon className="h-3 w-3" />
                   <span className="text-[10px] font-black uppercase tracking-widest">{table.capacity}</span>
                </div>
              </div>
              
              <div className={`w-3 h-3 rounded-full ring-4 shadow-sm ${
                table.status === 'occupied' ? 'bg-orange-500 ring-orange-100 animate-pulse' :
                table.status === 'reserved' ? 'bg-blue-500 ring-blue-100' :
                table.status === 'available' ? 'bg-emerald-500 ring-emerald-100' :
                'bg-gray-300 ring-gray-100'
              }`}></div>
            </div>

            <div className="mt-auto">
              {table.status === 'occupied' && table.currentOrder ? (
                 <div className="animate-in slide-in-from-bottom-2 duration-500">
                    <p className="text-[9px] font-black opacity-40 uppercase tracking-tighter mb-1 leading-none">Active Load</p>
                    <p className="text-base font-black text-gray-900 leading-none tracking-tight">₹{table.currentOrder.amount}</p>
                 </div>
              ) : (
                 <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] group-hover:text-gray-500 transition-colors">
                    {table.status}
                 </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {tables.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 bg-gray-50/50 rounded-[40px] border border-dashed border-gray-200">
          <div className="w-20 h-20 bg-white rounded-[24px] flex items-center justify-center shadow-lg mb-6 border border-gray-100">
            <span className="text-2xl grayscale">🍽️</span>
          </div>
          <p className="text-gray-900 font-black uppercase tracking-[0.2em] text-xs">Floor Inactive</p>
          <p className="text-gray-400 text-[10px] font-bold mt-2 uppercase tracking-widest">Configure nodes in settings</p>
        </div>
      )}
    </div>
  );
};

export default TableStatusGrid;
